import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import _ from 'lodash';
import { useEffect } from 'react';
import { SettingsAccount, useUpdateAccountSettingsMutation } from '../SettingsApi';
import { toast } from 'react-toastify';
import useUser from '@auth/useUser';
import { CircularProgress } from '@mui/material';

type FormType = SettingsAccount;

const defaultValues: FormType = {
	name: '',
	email: ''
};

/**
 * Form Validation Schema
 */
const schema = z.object({
	name: z.string().nonempty('Name is required'),
	email: z.string().email('Invalid email').nonempty('Email is required')
});

function AccountTab() {
	const { data: user } = useUser();
	const [updateAccountSettings, { isLoading }] = useUpdateAccountSettingsMutation();

	const { control, reset, handleSubmit, formState } = useForm<FormType>({
		defaultValues,
		mode: 'all',
		resolver: zodResolver(schema)
	});

	const { isValid, dirtyFields, errors } = formState;

	const setUserValues = () => {
		reset({
			name: user?.name || '',
			email: user?.email || ''
		});
	};

	useEffect(() => {
		if (user) {
			setUserValues();
		}
	}, [reset, user]);

	/**
	 * Form Submit
	 */
	async function onSubmit(formData: FormType) {
		try {
			const data = {
				...formData,
				user_id: user.user_id,
				_method: 'PUT'
			};
			const response: any = await updateAccountSettings(data);

			if (response?.error) {
				toast.error(response?.error?.data?.errors?.[0]?.title || 'Something went wrong!');
			} else {
				// Update user in localStorage instead of cookies
				const { setUser } = await import('@/utils/localStorage');
				const updatedUser = {
					...user,
					name: formData.name,
					email: formData.email
				};
				setUser(updatedUser);
				toast.success('Profile Updated Successfully!');
			}
		} catch (err) {
			console.log(err);
		}
	}

	return (
		<div className="w-full max-w-5xl">
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="w-full">
					<Typography className="text-xl">Personal Information</Typography>
					<Typography color="text.secondary">
						Communication details in case we want to connect with you. These will be kept private.
					</Typography>
				</div>
				<div className="grid w-full gap-6 sm:grid-cols-4 mt-8">
					<div className="sm:col-span-2">
						<Controller
							control={control}
							name="name"
							render={({ field }) => (
								<TextField
									{...field}
									label="Name"
									placeholder="Name"
									id="name"
									error={!!errors.name}
									helperText={errors?.name?.message}
									variant="outlined"
									required
									fullWidth
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<FuseSvgIcon size={20}>heroicons-solid:user-circle</FuseSvgIcon>
											</InputAdornment>
										)
									}}
								/>
							)}
						/>
					</div>
					<div className="sm:col-span-2">
						<Controller
							control={control}
							name="email"
							render={({ field }) => (
								<TextField
									{...field}
									label="Email"
									placeholder="Email"
									variant="outlined"
									fullWidth
									error={!!errors.email}
									helperText={errors?.email?.message}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<FuseSvgIcon size={20}>heroicons-solid:envelope</FuseSvgIcon>
											</InputAdornment>
										)
									}}
								/>
							)}
						/>
					</div>
				</div>

				<Divider className="mb-10 mt-11 border-t" />
				<div className="flex items-center justify-end space-x-2">
					<Button
						variant="outlined"
						disabled={_.isEmpty(dirtyFields) || isLoading}
						onClick={setUserValues}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="secondary"
						type="submit"
						disabled={_.isEmpty(dirtyFields) || !isValid || isLoading}
						startIcon={isLoading && <CircularProgress size={20} />}
					>
						Save
					</Button>
				</div>
			</form>
		</div>
	);
}

export default AccountTab;
